import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Loader2, ChevronLeft, ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { PostCard } from '../components/blog/PostCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANK_COLORS = {
  pro: "#4A90D9", elite: "#9B59B6", master: "#F1C40F",
  grandmaster: "#E74C3C", legendary: "#E07A5F"
};

export default function ProviderPortfolio() {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t } = useLanguage();
  const [provider, setProvider] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const [providerRes, postsRes] = await Promise.all([
          fetch(`${API_URL}/api/providers/${providerId}`),
          fetch(`${API_URL}/api/posts?provider_id=${providerId}`, { headers })
        ]);
        if (providerRes.ok) setProvider(await providerRes.json());
        if (postsRes.ok) setPosts(await postsRes.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [providerId, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!provider) {
    navigate('/providers');
    return null;
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="provider-portfolio">
      <div className="container-app max-w-2xl">
        {/* Back */}
        <Button variant="ghost" className="mb-6" onClick={() => navigate(`/providers/${providerId}`)}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          {t('blog.backToProfile')}
        </Button>

        {/* Provider Header */}
        <div className="flex items-center gap-4 mb-8 p-6 bg-card rounded-2xl border border-border/50 shadow-soft">
          <Avatar className="w-16 h-16">
            {provider.profile_image ? <AvatarImage src={provider.profile_image} /> : null}
            <AvatarFallback className="text-xl bg-accent text-accent-foreground">{provider.name?.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="font-heading text-xl font-semibold">{provider.name}</h1>
              <Badge style={{ backgroundColor: RANK_COLORS[provider.rank] }} className="text-white border-0 text-xs">
                {provider.rank}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm">{posts.length} {posts.length !== 1 ? t('blog.publications') : t('blog.publication')}</p>
          </div>
          <Link to={`/providers/${providerId}`}>
            <Button variant="outline" size="sm">{t('blog.viewProfile')}</Button>
          </Link>
        </div>

        {/* Posts */}
        {posts.length === 0 ? (
          <div className="text-center py-16">
            <ImageIcon className="w-16 h-16 text-muted-foreground/20 mx-auto mb-4" />
            <p className="text-muted-foreground">{t('blog.noPostsYet')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {posts.map(post => (
              <PostCard key={post.post_id} post={post} showProviderInfo={false} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
